import type { AnyRouter } from '@tanstack/react-router';
import { setKitTelemetry } from '@aimmo/ui';
import {
  fromKitEvent,
  installStats,
  trackEvent,
  trackPage,
  viewportOf,
  type Display,
} from './stats.js';

interface NavigatorStandalone extends Navigator {
  /** iOS's own flag; `display-mode` is not reported there for a home-screen app. */
  standalone?: boolean;
}

function displayMode(): Display {
  const standalone =
    globalThis.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as NavigatorStandalone).standalone === true;
  return standalone ? 'standalone' : 'browser';
}

/**
 * Starts the usage statistics (M17, section E): the scrubbing hook, one
 * `app-opened` per page load, and a page view for every route the router
 * resolves a new path for. A page view carries the route's template, so a
 * search typed into a list — which lives in the query — never reaches it.
 *
 * Harmless without a tracker on the page: every call below is a no-op then.
 */
export function startStats(router: AnyRouter): void {
  installStats(window);
  setKitTelemetry((event) => trackEvent(window, fromKitEvent(event)));
  trackEvent(window, {
    name: 'app-opened',
    data: { display: displayMode(), viewport: viewportOf(window.innerWidth) },
  });

  const page = (): void => {
    const leaf = router.state.matches.at(-1);
    if (leaf) trackPage(window, leaf.fullPath);
  };
  router.subscribe('onResolved', (event) => {
    if (event.pathChanged) page();
  });
  // The first page is not a change of path, and may already be resolved.
  if (router.state.status === 'idle' && router.state.matches.length > 0) page();
}
